import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  api,
  Button,
  Card,
  Field,
  AvatarImage,
  ImageUploadField,
  useAuth,
  useBrand,
  validateDisplayName,
  validatePhone,
  validateDateOfBirth,
  validateAddress,
  isAddressBlank,
  formatUsPhone,
  normalizeStateCode,
  daysInMonth,
  US_STATES,
  MONTHS,
  type AddressInput,
} from '@aralere/shared-ui';
import type {
  AccountProfile,
  UpdateAccountProfileRequest,
  AddressLike,
} from '@aralere/shared-types';

// ──────────────────────────────────────────────────────────────────────────────
// Build 2 — client self-service "Account settings" (04-deferred-register #2).
// Edits ONLY the caller's own record: the API takes no id (caller-own-record-scoped),
// so this page is a single-record form, not a picker. Editable: name, phone, primary
// address, DOB (consumers). Status / legal name / domain / billing are admin-only and
// deliberately absent. Form, validators, and UX mirror the Build-1 admin editor.
// ──────────────────────────────────────────────────────────────────────────────

type FormState = {
  name: string;
  companyName: string;
  phone: string;
  line1: string;
  line2: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  dobMonth: string;
  dobDay: string;
  dobYear: string;
};

type FieldErrors = Partial<Record<keyof FormState | 'dob', string>>;

function formFromProfile(p: AccountProfile): FormState {
  const a = p.address;
  const dob = (p.dateOfBirth ?? '').split('-');
  const hasDob = dob.length === 3;
  return {
    name: p.name,
    companyName: p.companyName ?? '',
    phone: formatUsPhone(p.phone ?? ''),
    line1: a?.line1 ?? '',
    line2: a?.line2 ?? '',
    city: a?.city ?? '',
    region: normalizeStateCode(a?.region ?? ''),
    postalCode: a?.postalCode ?? '',
    country: a?.country ?? '',
    dobMonth: hasDob ? String(Number(dob[1])) : '',
    dobDay: hasDob ? String(Number(dob[2])) : '',
    dobYear: hasDob ? dob[0] : '',
  };
}

const pad2 = (s: string): string => s.padStart(2, '0');

function composeDob(f: FormState): string {
  if (f.dobMonth && f.dobDay && f.dobYear) return `${f.dobYear}-${pad2(f.dobMonth)}-${pad2(f.dobDay)}`;
  return '';
}

function dobPartial(f: FormState): boolean {
  const filled = [f.dobMonth, f.dobDay, f.dobYear].filter(Boolean).length;
  return filled > 0 && filled < 3;
}

function addressInput(f: FormState): AddressInput {
  return {
    line1: f.line1, line2: f.line2, city: f.city,
    region: f.region, postalCode: f.postalCode, country: f.country,
  };
}

function validateForm(f: FormState, profile: AccountProfile): FieldErrors {
  const errors: FieldErrors = {};
  const set = (k: keyof FormState, e: string | null): void => {
    if (e) errors[k] = e;
  };
  // The person's own name is always editable.
  set('name', validateDisplayName(f.name));
  if (!profile.canEdit) return errors; // non-primary users edit only their name

  if (profile.clientType === 'business') set('companyName', validateDisplayName(f.companyName));
  set('phone', validatePhone(f.phone));
  if (profile.clientType === 'consumer') {
    if (dobPartial(f)) errors.dob = 'Select month, day, and year.';
    else {
      const e = validateDateOfBirth(composeDob(f));
      if (e) errors.dob = e;
    }
  }
  const addrErrors = validateAddress(addressInput(f));
  for (const [k, v] of Object.entries(addrErrors)) {
    errors[k as keyof FormState] = v;
  }
  return errors;
}

/** Build the PATCH from CHANGED fields only. */
function buildPatch(f: FormState, p: AccountProfile): UpdateAccountProfileRequest {
  const patch: UpdateAccountProfileRequest = {};
  const a = p.address;

  // The person's own name — any associated user may change it.
  if (f.name.trim() !== p.name) patch.name = f.name.trim();

  if (!p.canEdit) return patch; // non-primary users can only change their name

  if (p.clientType === 'business' && f.companyName.trim() !== (p.companyName ?? '')) {
    patch.companyName = f.companyName.trim();
  }
  if ((f.phone.trim() || null) !== (p.phone ?? null)) patch.phone = f.phone.trim() || null;

  const blank = isAddressBlank(addressInput(f));
  const next: AddressLike | null = blank
    ? null
    : {
        type: a?.type ?? 'main',
        line1: f.line1.trim(),
        line2: f.line2.trim() || null,
        city: f.city.trim(),
        region: f.region.trim(),
        postalCode: f.postalCode.trim(),
        country: f.country.trim(),
      };
  const prev: AddressLike | null = a ?? null;
  if (JSON.stringify(next) !== JSON.stringify(prev)) patch.address = next;

  if (p.clientType === 'consumer') {
    const oldDob = p.dateOfBirth ?? null;
    const newDob = composeDob(f) || null;
    if (newDob !== oldDob) patch.dateOfBirth = newDob;
  }

  return patch;
}

export function AccountSettings(): JSX.Element {
  const { user, activeClient, refresh } = useAuth();
  const [profile, setProfile] = useState<AccountProfile | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [syncWarning, setSyncWarning] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Profile photo / company logo (04 #3). Each mutation calls the SDK then refresh() (so the
  // header + context pick up the new ref) and bumps imgBust to defeat the serve route's short cache.
  const [imgBust, setImgBust] = useState(0);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [imgError, setImgError] = useState<string | null>(null);

  const afterImageChange = async (): Promise<void> => {
    await refresh();
    setImgBust((n) => n + 1);
  };
  const runImage = async (fn: () => Promise<unknown>, setBusy: (b: boolean) => void): Promise<void> => {
    setImgError(null);
    setBusy(true);
    try {
      await fn();
      await afterImageChange();
    } catch (e) {
      setImgError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    api.account
      .getProfile()
      .then(({ profile: p }) => {
        if (cancelled) return;
        setProfile(p);
        setForm(formFromProfile(p));
      })
      .catch((e: Error) => {
        if (!cancelled) setLoadError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Live validation: every change re-validates the whole form (address fields interact).
  const update = (patch: Partial<FormState>): void => {
    if (!form || !profile) return;
    const next = { ...form, ...patch };
    setForm(next);
    setFieldErrors(validateForm(next, profile));
    setSaved(false);
  };

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!form || !profile) return;

    const errors = validateForm(form, profile);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setFormError('Fix the highlighted fields before saving.');
      return;
    }

    const patch = buildPatch(form, profile);
    if (Object.keys(patch).length === 0) {
      setSaved(true);
      setFormError(null);
      return;
    }

    setSubmitting(true);
    setFormError(null);
    setSyncWarning(null);
    setSaved(false);
    try {
      const res = await api.account.updateProfile(patch);
      setProfile(res.profile);
      setForm(formFromProfile(res.profile));
      if (!res.entraSync.ok) setSyncWarning(res.entraSync.errors);
      setSaved(true);
    } catch (err) {
      setFormError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const isConsumer = profile?.clientType === 'consumer';
  const isBusiness = profile?.clientType === 'business';
  const isGuest = user?.tenancyModel === 'b2b-guest';

  // s33 linked-account model: a consumer account linked to a business is managed by that business —
  // services + billing are controlled there. We derive `managed` from the session chrome (activeClient is
  // the business identity when linked), which is loaded before this page mounts, so the managed notice
  // paints with everything else rather than popping in after the async profile fetch. The person's own
  // personal details stay editable here (this is still their own account), so we only surface a "managed
  // by your organization" notice. There is no billing section on this page today (billing is Stage 4);
  // when one is added it should be gated on `managed`. The business name comes from activeClient, else the
  // generic fallback. (account-profile still returns managedByBusiness as the server-side source of truth.)
  const managed = activeClient?.clientType === 'business';
  const managerLabel = managed && activeClient?.displayName ? activeClient.displayName : 'your organization';

  const syncNote = isBusiness
    ? 'Changes here update your records with us. Your Microsoft sign-in account is unchanged.'
    : isGuest
      ? 'Changes here update your records with us (your Microsoft account is managed by your own organization).'
      : 'Your name, phone, and address also update your Microsoft sign-in profile.';

  return (
    <div className="space-y-6 max-w-3xl">
      <header>
        <h1 className="font-display text-3xl tracking-tight">Account settings</h1>
        <p className="text-sm text-[var(--color-muted)]">
          Keep your contact details up to date.
        </p>
      </header>

      {managed ? (
        <Card>
          <div className="space-y-1">
            <p className="text-sm font-medium text-[var(--color-text)]">
              Your account is managed by {managerLabel}.
            </p>
            <p className="text-sm text-[var(--color-muted)]">
              They handle your services and billing. You can still keep your personal details up to date below.
            </p>
          </div>
        </Card>
      ) : null}

      {loading ? <p className="text-sm text-[var(--color-muted)]">Loading…</p> : null}

      {loadError ? (
        <Card>
          <p className="text-sm text-[var(--color-muted)]">
            We couldn’t find an account profile for your sign-in on this portal. If that seems
            wrong, please contact us.
          </p>
        </Card>
      ) : null}

      {profile && user && !loadError ? (
        <Card>
          <div className="space-y-6">
            <div>
              <h2 className="font-display text-xl">Photo &amp; logo</h2>
              <p className="text-sm text-[var(--color-muted)]">A JPG, PNG, or WebP up to 5 MB.</p>
            </div>

            <ImageUploadField
              label="Profile photo"
              hint="Shown on your account. Square images look best."
              shape="circle"
              name={user.displayName}
              hasImage={!!user.photoBlobRef}
              src={user.photoBlobRef ? `/api/avatar?userId=${encodeURIComponent(user.id)}&v=${imgBust}` : null}
              busy={avatarBusy}
              onSelect={(file) => void runImage(() => api.account.uploadAvatar(file), setAvatarBusy)}
              onRemove={() => void runImage(() => api.account.deleteAvatar(), setAvatarBusy)}
            />

            {isBusiness ? (
              profile.canEdit ? (
                <ImageUploadField
                  label="Company logo"
                  hint="Shown in the portal header and on your account."
                  shape="square"
                  name={profile.companyName ?? ''}
                  hasImage={!!activeClient?.logoBlobRef}
                  src={
                    activeClient?.logoBlobRef
                      ? `/api/client-logo?clientId=${encodeURIComponent(activeClient.clientId)}&v=${imgBust}`
                      : null
                  }
                  busy={logoBusy}
                  onSelect={(file) => void runImage(() => api.account.uploadLogo(file), setLogoBusy)}
                  onRemove={() => void runImage(() => api.account.deleteLogo(), setLogoBusy)}
                />
              ) : (
                <div>
                  <p className="text-sm font-medium mb-2">Company logo</p>
                  <div className="flex items-center gap-4">
                    <AvatarImage
                      src={
                        activeClient?.logoBlobRef
                          ? `/api/client-logo?clientId=${encodeURIComponent(activeClient.clientId)}&v=${imgBust}`
                          : null
                      }
                      name={profile.companyName ?? ''}
                      size={80}
                      shape="square"
                    />
                    <p className="text-xs text-[var(--color-muted)]">Managed by your account owner.</p>
                  </div>
                </div>
              )
            ) : null}

            {imgError ? <p className="text-sm text-[var(--tint-red-text)]">{imgError}</p> : null}
          </div>
        </Card>
      ) : null}

      {syncWarning ? (
        <div className="rounded-md bg-[var(--tint-warn-bg-soft)] border border-[var(--tint-warn-border)] px-4 py-3 text-sm text-[var(--tint-warn-text)]">
          <p className="font-medium">Saved, but the Microsoft (Entra) sync had a problem:</p>
          <ul className="mt-1 list-disc pl-5">
            {syncWarning.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
          <p className="mt-1">Your changes are saved in our records. Re-save to retry the sync.</p>
        </div>
      ) : null}

      {profile && form ? (
        <Card>
          <form onSubmit={submit} className="space-y-5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-xs text-[var(--color-muted)]">{syncNote}</p>
              <p className="text-xs text-[var(--color-muted)]">
                Member since {profile.memberSince.slice(0, 4)}
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Field label={isBusiness ? 'Your name' : 'Name'} required error={fieldErrors.name}>
                <Input
                  value={form.name}
                  onChange={(v) => update({ name: v })}
                  error={!!fieldErrors.name}
                  required
                />
              </Field>
              {isBusiness ? (
                profile.canEdit ? (
                  <Field label="Company name" required error={fieldErrors.companyName}>
                    <Input
                      value={form.companyName}
                      onChange={(v) => update({ companyName: v })}
                      error={!!fieldErrors.companyName}
                      required
                    />
                  </Field>
                ) : (
                  <Field label="Company name">
                    <ReadOnlyValue>{profile.companyName ?? '-'}</ReadOnlyValue>
                  </Field>
                )
              ) : null}
              <Field label="Contact email">
                <p className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.03)] px-3 py-2 text-sm text-[var(--color-muted)]">
                  {profile.email}
                </p>
                <span className="mt-1 block text-xs text-[var(--color-muted)]">
                  {isGuest
                    ? 'You sign in with this email. Contact us if it needs to change.'
                    : 'Contact us if it needs to change.'}
                </span>
              </Field>
              {profile.signInUsername ? (
                <Field label="Sign-in username">
                  <p className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.03)] px-3 py-2 text-sm text-[var(--color-muted)]">
                    {profile.signInUsername}
                  </p>
                  <span className="mt-1 block text-xs text-[var(--color-muted)]">
                    This is what you use to sign in to the portal.
                  </span>
                </Field>
              ) : null}
            </div>

            {profile.canEdit ? (
              <>
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Phone" error={fieldErrors.phone}>
                    <Input
                      value={form.phone}
                      onChange={(v) => update({ phone: formatUsPhone(v) })}
                      error={!!fieldErrors.phone}
                      placeholder="(555) 201-3344"
                    />
                  </Field>
                  {isConsumer ? (
                    <Field label="Date of birth" error={fieldErrors.dob}>
                      <DobPicker
                        month={form.dobMonth}
                        day={form.dobDay}
                        year={form.dobYear}
                        error={!!fieldErrors.dob}
                        onChange={(p) => update(p)}
                      />
                    </Field>
                  ) : null}
                </div>

                <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-4 space-y-4">
                  <p className="text-sm font-medium">Primary address (leave fully blank for none)</p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Address line 1" error={fieldErrors.line1}>
                      <Input value={form.line1} onChange={(v) => update({ line1: v })} error={!!fieldErrors.line1} placeholder="123 Main St" />
                    </Field>
                    <Field label="Address line 2" error={fieldErrors.line2}>
                      <Input value={form.line2} onChange={(v) => update({ line2: v })} error={!!fieldErrors.line2} placeholder="Suite 400" />
                    </Field>
                  </div>
                  <div className="grid gap-4 md:grid-cols-4">
                    <Field label="City" error={fieldErrors.city}>
                      <Input value={form.city} onChange={(v) => update({ city: v })} error={!!fieldErrors.city} />
                    </Field>
                    <Field label="State / region" error={fieldErrors.region}>
                      <Select
                        value={form.region}
                        onChange={(v) => update({ region: v })}
                        options={[['', '-'] as const, ...US_STATES]}
                      />
                    </Field>
                    <Field label="Postal code" error={fieldErrors.postalCode}>
                      <Input value={form.postalCode} onChange={(v) => update({ postalCode: v })} error={!!fieldErrors.postalCode} placeholder="90001" />
                    </Field>
                    <Field label="Country" error={fieldErrors.country}>
                      <Input value={form.country} onChange={(v) => update({ country: v })} error={!!fieldErrors.country} placeholder="US" />
                    </Field>
                  </div>
                </div>
              </>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Phone">
                  <ReadOnlyValue>{profile.phone ?? '-'}</ReadOnlyValue>
                </Field>
                <Field label="Primary address">
                  <ReadOnlyValue>
                    {profile.address
                      ? [profile.address.line1, profile.address.line2, profile.address.city,
                         profile.address.region, profile.address.postalCode, profile.address.country]
                          .filter(Boolean)
                          .join(', ')
                      : '-'}
                  </ReadOnlyValue>
                </Field>
              </div>
            )}

            {!profile.canEdit ? (
              <p className="text-xs text-[var(--color-muted)]">
                Your name is yours to edit. Your account owner manages the other details; contact us
                if something needs updating.
              </p>
            ) : null}

            {formError ? (
              <div className="rounded-md bg-[var(--tint-red-bg-soft)] border border-[var(--tint-red-border)] px-4 py-3 text-sm text-[var(--tint-red-text)]">
                {formError}
              </div>
            ) : null}

            {saved && !formError ? (
              <div className="rounded-md bg-[var(--tint-green-bg-soft)] border border-[var(--tint-green-border)] px-4 py-3 text-sm text-[var(--tint-green-text)]">
                Changes saved.
              </div>
            ) : null}

            <div className="flex gap-3 pt-1">
              <Button
                type="submit"
                loading={submitting}
                disabled={submitting || Object.keys(fieldErrors).length > 0}
              >
                Save changes
              </Button>
              <Link to="/">
                <Button type="button" variant="ghost" disabled={submitting}>
                  Cancel
                </Button>
              </Link>
            </div>
          </form>
        </Card>
      ) : null}

      <WelcomeReplayCard />
    </div>
  );
}

function WelcomeReplayCard(): JSX.Element | null {
  const { brand } = useBrand();
  const [busy, setBusy] = useState(false);
  const wm = brand?.welcomeMessage;
  const enabled = !!wm && wm.enabled;
  if (!enabled) return null; // nothing to replay when this brand has no welcome message

  const replay = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.welcome.reset();
      window.location.assign('/'); // reload home → the welcome modal re-checks and reappears
    } catch {
      setBusy(false); // on failure, stay put (the reload won't happen)
    }
  };

  return (
    <Card>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-xl">Welcome message</h2>
          <p className="text-sm text-[var(--color-muted)]">Replay your portal's welcome message.</p>
        </div>
        <Button type="button" variant="ghost" onClick={() => void replay()} loading={busy}>
          Show again
        </Button>
      </div>
    </Card>
  );
}

function ReadOnlyValue({ children }: { children: ReactNode }): JSX.Element {
  return (
    <p className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.03)] px-3 py-2 text-sm text-[var(--color-muted)]">
      {children}
    </p>
  );
}

function DobPicker({
  month,
  day,
  year,
  error,
  onChange,
}: {
  month: string;
  day: string;
  year: string;
  error?: boolean;
  onChange: (patch: { dobMonth?: string; dobDay?: string; dobYear?: string }) => void;
}): JSX.Element {
  const thisYear = new Date().getFullYear();
  const years = Array.from({ length: 120 }, (_, i) => String(thisYear - i));
  const maxDay = daysInMonth(year ? Number(year) : null, month ? Number(month) : 0);
  const days = Array.from({ length: maxDay }, (_, i) => String(i + 1));

  const clampDay = (nextMonth: string, nextYear: string): string => {
    if (!day) return day;
    const max = daysInMonth(nextYear ? Number(nextYear) : null, nextMonth ? Number(nextMonth) : 0);
    return Number(day) > max ? String(max) : day;
  };

  const selectCls = [
    'rounded-md border px-3 py-2 text-sm bg-[var(--surface-solid)]',
    error ? 'border-[var(--tint-red-border)]' : 'border-[rgb(var(--edge-rgb)/0.10)]',
  ].join(' ');

  return (
    <div className="grid grid-cols-3 gap-2">
      <select
        aria-label="Birth month"
        className={selectCls}
        value={month}
        onChange={(e) => onChange({ dobMonth: e.target.value, dobDay: clampDay(e.target.value, year) })}
      >
        <option value="">Month</option>
        {MONTHS.map(([v, label]) => (
          <option key={v} value={v}>{label}</option>
        ))}
      </select>
      <select
        aria-label="Birth day"
        className={selectCls}
        value={day}
        onChange={(e) => onChange({ dobDay: e.target.value })}
      >
        <option value="">Day</option>
        {days.map((d) => (
          <option key={d} value={d}>{d}</option>
        ))}
      </select>
      <select
        aria-label="Birth year"
        className={selectCls}
        value={year}
        onChange={(e) => onChange({ dobYear: e.target.value, dobDay: clampDay(month, e.target.value) })}
      >
        <option value="">Year</option>
        {years.map((y) => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
  );
}


function Input({
  value,
  onChange,
  placeholder,
  type = 'text',
  required,
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  error?: boolean;
}): JSX.Element {
  return (
    <input
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={[
        'w-full rounded-md border px-3 py-2 text-sm bg-[var(--surface-solid)]',
        error ? 'border-[var(--tint-red-border)]' : 'border-[rgb(var(--edge-rgb)/0.10)]',
      ].join(' ')}
    />
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<readonly [string, string]>;
}): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
    >
      {options.map(([v, label]) => (
        <option key={v} value={v}>
          {label}
        </option>
      ))}
    </select>
  );
}
